import React from "react"

import type { AudioRegionReviewConfig, MetricRecord } from "@/metrics/runtime"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type { Annotation } from "@/types/api/Annotation"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"

import { LoopDetectionConfig } from "../../configuration/components/LoopDetectionConfig"
import { AudioRegionGroundTruthDisplay } from "../../hooks/useAudioRegionReview"
import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { AnomalyEventList } from "../components/AnomalyEventList"
import { getCardStyle } from "../components/deepDiveCardStyles"
import { WithAudioFilterMixin } from "../mixins/WithAudioFilterMixin"

interface LoopDetectionResultJson {
  loop_total_duration_seconds?: number
  max_loop_duration_seconds?: number
  loop_acoustic_similarity?: number
}

const CATEGORY = "loop_detection"
// Tuned against the reweighted severity formula so short keyclick-style
// bursts (~0.30 severity) hide while sentence-length loops (>=4.5s span)
// stay visible. Override per-metric via display_severity_threshold.
const DEFAULT_DISPLAY_SEVERITY_THRESHOLD = 0.4

function getDisplaySeverityThreshold(
  metricMetadata: { display_severity_threshold?: number } | undefined,
): number {
  const value = metricMetadata?.display_severity_threshold
  if (typeof value === "number" && value >= 0 && value <= 1) {
    return value
  }
  return DEFAULT_DISPLAY_SEVERITY_THRESHOLD
}

const REPEATED_SEGMENT_ANOMALY_TYPE = "repeated_segment"

function getLoopAnomalyType(sv: SubvalueByTimestamp): string | undefined {
  return sv.metadata?.anomaly_type as string | undefined
}

// Blue for TTS Loops (speech-rate), teal for Repeated Sounds (typing/UI).
function getLoopRegionColor(sv: SubvalueByTimestamp): string {
  const severity = sv.float_value ?? 0
  const alpha = Math.min(0.2 + severity * 0.5, 0.7).toFixed(2)
  return getLoopAnomalyType(sv) === REPEATED_SEGMENT_ANOMALY_TYPE
    ? `rgba(20, 184, 166, ${alpha})`
    : `rgba(59, 130, 246, ${alpha})`
}

const TTS_LOOP_CHIP_CLASSES =
  "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800"
const REPEATED_SOUND_CHIP_CLASSES =
  "bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-800"

function getLoopChipColorClass(sv: SubvalueByTimestamp): string {
  return getLoopAnomalyType(sv) === REPEATED_SEGMENT_ANOMALY_TYPE
    ? REPEATED_SOUND_CHIP_CLASSES
    : TTS_LOOP_CHIP_CLASSES
}

export class LoopDetectionMetricRecord extends WithAudioFilterMixin(
  NumericalMetricRecord,
) {
  overviewSize = "medium" as const
  unit = "count"
  aggregate_units = "count"

  renderMetricType(): string {
    return "Loop Detection"
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const resultJson = (mo.result as Record<string, unknown> | undefined)
      ?.raw_values as LoopDetectionResultJson | undefined
    const subvalues = mo.subvalues_by_timestamp ?? []
    const loopCount = mo.float_value ?? 0
    const totalDuration = resultJson?.loop_total_duration_seconds
    const maxLoop = resultJson?.max_loop_duration_seconds
    const card = getCardStyle(totalDuration ?? 0, 0.5)
    const severityThreshold = getDisplaySeverityThreshold(this.metric_metadata)

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className={`rounded-lg p-3 ${card.bg}`}>
            <div className={card.label}>Loops Detected</div>
            <div className={card.value}>{Math.round(loopCount)}</div>
          </div>
          {totalDuration != null && (
            <div className={`rounded-lg p-3 ${card.bg}`}>
              <div className={card.label}>Total Duration</div>
              <div className={card.value}>{totalDuration.toFixed(1)}s</div>
            </div>
          )}
          {maxLoop != null && (
            <div className={`rounded-lg p-3 ${card.bg}`}>
              <div className={card.label}>Max Loop</div>
              <div className={card.value}>{maxLoop.toFixed(1)}s</div>
            </div>
          )}
        </div>

        <AnomalyEventList
          subvalues={subvalues}
          severityThreshold={severityThreshold}
          chipLabel="Loop"
          title="Detected Loops"
          groupByLabel
          regionColorFn={getLoopRegionColor}
          chipColorClassFn={getLoopChipColorClass}
        />

        {subvalues.length === 0 && (
          <div className="text-sm text-coval-text-muted">No loops detected</div>
        )}
      </div>
    )
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message) {
      return []
    }
    try {
      const subvalues = mo.subvalues_by_timestamp ?? []
      const severityThreshold = getDisplaySeverityThreshold(
        this.metric_metadata,
      )
      return subvalues
        .filter((item) => (item.float_value ?? 0) >= severityThreshold)
        .map((item, idx) => ({
          id: `${CATEGORY}:${idx}`,
          start: item.start_offset,
          end: Math.max(item.end_offset, item.start_offset + 0.5),
          color: getLoopRegionColor(item),
          drag: false,
          resize: false,
        }))
    } catch {
      return []
    }
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "raw_count",
      buildReviewedResultJson: ({ durationSeconds, regions }) => {
        const totalDuration = regions.reduce(
          (sum, r) => sum + ((r.end ?? r.start + 0.05) - r.start),
          0,
        )
        const maxLoop = regions.reduce(
          (max, r) => Math.max(max, (r.end ?? r.start + 0.05) - r.start),
          0,
        )
        return {
          loop_total_duration_seconds: Number(totalDuration.toFixed(2)),
          max_loop_duration_seconds: Number(maxLoop.toFixed(2)),
          duration_seconds:
            durationSeconds != null ? Number(durationSeconds.toFixed(2)) : null,
        }
      },
    }
  }

  renderAssignmentsTableReviewCell(
    _simulationOutputId: string,
    annotationsForMetric: Annotation[],
    _projectId?: string,
    _metricOutput?: RunMetricOutput,
  ): React.ReactNode {
    return (
      <div className="flex flex-col items-center gap-1">
        <AudioRegionGroundTruthDisplay
          annotationsForMetric={annotationsForMetric}
          metricRecord={this}
        />
      </div>
    )
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    textAreaRef?: React.RefObject<HTMLTextAreaElement>,
    editorContext?: Parameters<MetricRecord["renderEditorMetadata"]>[3],
  ): React.ReactNode {
    return (
      <div className="flex flex-col gap-4">
        <LoopDetectionConfig
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
        {super.renderEditorMetadata(
          metricRecordData,
          onChange,
          textAreaRef,
          editorContext,
        )}
      </div>
    )
  }
}
